/**
 * Sheet primitive — side-panel variant over `@radix-ui/react-dialog`.
 *
 * Same Radix substrate as `Dialog.tsx` (portal, focus trap, escape-key
 * dismissal, overlay click dismissal, ARIA role/title/description
 * association all come from Radix). The only differences are layout —
 * Sheet pins its content to a screen edge and fills the perpendicular
 * axis — and the `side` prop that selects which edge.
 *
 * Compound-component shape mirrors `Dialog.tsx` exactly (see its header
 * for the rationale): dedicated named exports `Root` / `Trigger` /
 * `Content` / `Title` / `Description` / `Close`, plus an aggregate
 * `Sheet` object. `Content` embeds Radix's `Portal` + `Overlay`
 * internally; they are not part of the public surface to keep the
 * composition trap-free. Only `Content` diverges from the Dialog file —
 * it carries the side-prop-driven positioning class lookup.
 *
 * `side` prop values:
 *
 *   - `'right'` (default): `fixed right-0 top-0 h-full w-full max-w-xs`.
 *     Pins to the right edge, full viewport height, constrained width.
 *     Slides in from the right.
 *   - `'left'`: `fixed left-0 top-0 h-full w-full max-w-xs`. Pins to the
 *     left edge with the same height/width constraints.
 *
 * `'top'` and `'bottom'` are deliberately NOT in the Cluster 1 scope;
 * when a user story demands a top or bottom sheet, this file extends
 * the lookup table with the corresponding `top-0 left-0 w-full
 * max-h-sm` / `bottom-0 left-0 w-full max-h-sm` entries. The shape is
 * designed to accept that extension without a rewrite.
 *
 * Every class below is a token-backed Tailwind utility — no
 * arbitrary-bracket classes (FR-047), no raw colour literals (FR-002).
 * Tailwind v4's default utility set covers `fixed`, `inset-0`,
 * `right-0` / `left-0`, `top-0`, `h-full`, `w-full`, `max-w-xs`,
 * `shadow-xl`, and the `transition-transform` / `duration-200` /
 * `ease-out` motion utilities out of the box.
 *
 * Animation: `transition-transform duration-200 ease-out` on Content
 * plus Radix's `data-[state]` attributes would normally drive a
 * translate-in animation. Cluster 1 keeps this minimal — we apply the
 * transition classes but do NOT add bracketed `data-[state=closed]:…`
 * selectors (FR-047 bans bracket classes). Richer motion can be added
 * via CSS in a future iteration; this primitive's animation surface
 * stays token-safe today. `prefers-reduced-motion` is honoured
 * automatically by Tailwind v4's transition utilities.
 */

import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { ComponentProps } from 'react';

import { cn } from './utils.js';

export type SheetSide = 'left' | 'right';

export interface SheetContentProps extends ComponentProps<typeof DialogPrimitive.Content> {
  side?: SheetSide;
}
export type SheetTitleProps = ComponentProps<typeof DialogPrimitive.Title>;
export type SheetDescriptionProps = ComponentProps<typeof DialogPrimitive.Description>;
export type SheetCloseProps = ComponentProps<typeof DialogPrimitive.Close>;

const overlayClasses =
  'fixed inset-0 z-40 bg-foreground/70 transition-opacity duration-200 ease-out';

const contentBaseClasses =
  'fixed top-0 z-50 h-full w-full max-w-xs ' +
  'bg-popover text-popover-foreground border-border shadow-xl ' +
  'p-4 ' +
  'transition-transform duration-200 ease-out ' +
  'focus-visible:outline-none';

const sideClasses = {
  left: 'left-0 border-r',
  right: 'right-0 border-l',
} as const satisfies Record<SheetSide, string>;

const titleClasses = 'text-lg font-semibold text-foreground';

const descriptionClasses = 'text-sm text-muted-foreground';

const closeClasses =
  'inline-flex items-center justify-center rounded-md ' +
  'transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';

export const Root = DialogPrimitive.Root;
export const Trigger = DialogPrimitive.Trigger;

export function Content({ side = 'right', className, children, ...rest }: SheetContentProps) {
  return (
    <DialogPrimitive.Portal>
      {/*
       * `data-sheet="overlay" | "content"` tags the two Radix Dialog
       * elements so the entry/exit animations declared in
       * `apps/web/src/shell/shell.css` can be scoped to Sheet surfaces
       * without leaking onto unrelated Dialog consumers. Radix's
       * `data-state="open" | "closed"` and our `data-side` attributes
       * remain the primary animation discriminators.
       */}
      <DialogPrimitive.Overlay data-sheet="overlay" className={overlayClasses} />
      <DialogPrimitive.Content
        data-sheet="content"
        data-side={side}
        className={cn(contentBaseClasses, sideClasses[side], className)}
        {...rest}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function Title({ className, ...rest }: SheetTitleProps) {
  return <DialogPrimitive.Title className={cn(titleClasses, className)} {...rest} />;
}

export function Description({ className, ...rest }: SheetDescriptionProps) {
  return <DialogPrimitive.Description className={cn(descriptionClasses, className)} {...rest} />;
}

export function Close({ className, ...rest }: SheetCloseProps) {
  return <DialogPrimitive.Close className={cn(closeClasses, className)} {...rest} />;
}

export const Sheet = {
  Root,
  Trigger,
  Content,
  Title,
  Description,
  Close,
} as const;
